import React from "react";
import { motion } from "framer-motion";
import { Eye, BookOpen, Sparkles, Cat } from "lucide-react";
import { CATS } from "../../data/pusheen";

const inView = { once: true, amount: 0.3 };
const card = {
  hidden: { opacity: 0, y: 30, rotate: 0 },
  show: (r = 0) => ({ opacity: 1, y: 0, rotate: r, transition: { type: "spring", stiffness: 160, damping: 18 } }),
};

const sticker =
  "bg-white border-[3px] border-pusheen-brown rounded-3xl shadow-[5px_5px_0_#4A3728] hover:shadow-[8px_8px_0_#4A3728] hover:-translate-y-1 transition-all duration-200";

export default function Lore() {
  return (
    <section id="lore" className="relative z-10 py-20 sm:py-28 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={inView}
          className="text-center mb-14"
        >
          <p className="font-display font-bold text-pusheen-pink text-lg mb-2">the story so far</p>
          <h2 className="font-script text-4xl sm:text-5xl text-pusheen-brown">Meet the legend</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 sm:gap-8">
          {/* Big stat card */}
          <motion.div
            variants={card} custom={-2} initial="hidden" whileInView="show" viewport={inView}
            data-testid="lore-views"
            className={`${sticker} md:col-span-4 p-8 sm:p-10 flex items-center gap-6`}
          >
            <div className="shrink-0 bg-pusheen-mint border-[3px] border-pusheen-brown rounded-2xl p-3">
              <Eye className="w-8 h-8 text-pusheen-brown" />
            </div>
            <div>
              <p className="font-display font-extrabold text-5xl sm:text-6xl text-pusheen-brown leading-none">~60B</p>
              <p className="font-display font-bold text-pusheen-brown mt-2">One of GIPHY&apos;s biggest icons</p>
              <p className="font-body text-pusheen-brownlight mt-1 text-lg">
                With nearly 60 billion views, Pusheen is the <strong className="text-pusheen-brown">most-viewed cat</strong> on the platform — a testament to more than a decade of lasting popularity.
              </p>
            </div>
          </motion.div>

          {/* Dictionary card */}
          <motion.div
            variants={card} custom={2} initial="hidden" whileInView="show" viewport={inView}
            data-testid="lore-name"
            className={`${sticker} md:col-span-2 p-8 relative overflow-hidden`}
          >
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-pusheen-pink" />
              <span className="font-display font-bold text-pusheen-brownlight">the name</span>
            </div>
            <p className="font-script text-3xl text-pusheen-brown">puisín</p>
            <p className="font-body text-sm text-pusheen-brownlight italic">/ˈpʊʃiːn/ · Irish</p>
            <p className="font-body text-pusheen-brownlight mt-3">Meaning <strong className="text-pusheen-brown">“kitten,”</strong> the name perfectly captures the character&apos;s soft, round, and lovable personality.</p>
            <img src={CATS.heart} alt="" className="absolute -bottom-3 -right-3 w-20 opacity-80 animate-bob" />
          </motion.div>

          {/* Origin story card */}
          <motion.div
            variants={card} custom={-1} initial="hidden" whileInView="show" viewport={inView}
            data-testid="lore-history"
            className={`${sticker} md:col-span-3 p-8 flex flex-col`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Cat className="w-5 h-5 text-pusheen-pink" />
              <span className="font-display font-bold text-pusheen-brownlight">since 2010</span>
            </div>
            <p className="font-body text-pusheen-brownlight text-lg leading-relaxed">
              The story began in <strong className="text-pusheen-brown">2010</strong>. Created by <strong className="text-pusheen-brown">Claire Belton</strong>, Pusheen quickly became one of the internet&apos;s most beloved characters — bringing joy through comics, GIFs, stickers, books, and animations enjoyed by millions worldwide.
            </p>
            <img src={CATS.cookie} alt="" className="w-16 self-end mt-4 animate-wiggle" />
          </motion.div>

          {/* More than a meme card */}
          <motion.div
            variants={card} custom={1.5} initial="hidden" whileInView="show" viewport={inView}
            data-testid="lore-culture"
            className={`md:col-span-3 p-8 flex flex-col justify-center bg-pusheen-pink border-[3px] border-pusheen-brown rounded-3xl shadow-[5px_5px_0_#4A3728] hover:-translate-y-1 transition-all`}
          >
            <Sparkles className="w-7 h-7 text-pusheen-brown mb-3" />
            <p className="font-script text-3xl sm:text-4xl text-pusheen-brown leading-snug">More than a meme.</p>
            <p className="font-body text-pusheen-brown/80 mt-3">
              For over a decade, Pusheen has remained a cultural icon — not because of trends, but because people genuinely love sharing her. From everyday conversations to celebrations, she&apos;s become one of the internet&apos;s most recognizable faces.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
